import * as logger from '../../datadog-logger';
import { fetchCsrfToken } from '../../csrf-token-module.js';
import { createFeedbackDialog } from '../feedback-dialog-module.js';

const RELOAD_MESSAGE = 'Deine Sitzung ist abgelaufen. Bitte lade die Seite neu und versuche es erneut.';

/** Thrown when the server rejects the CSRF token, i.e. the session is stale. */
export class CsrfRejectedError extends Error {
  constructor() {
    super('CSRF token rejected');
    this.name = 'CsrfRejectedError';
  }
}

/**
 * Thin wrapper over native fetch that injects the mandatory CSRF header.
 * `async` so a missing token rejects the promise instead of throwing synchronously,
 * giving callers a single error path.
 *
 * @param {string|URL} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
export async function csrfFetch(url, init = {}) {
  const headers = new Headers(init.headers ?? {});
  headers.set('X-CSRF-Token', fetchCsrfToken());

  return fetch(url, { ...init, headers });
}

/**
 * Fetch a JSON endpoint via csrfFetch and resolve with the parsed body. On any
 * transport/HTTP/CSRF error it logs and rejects with no UX side effect: callers wanting the
 * standard dialog use {@link csrfFetchJsonWithFeedback}, callers wanting bespoke handling catch
 * this directly (branch on {@link CsrfRejectedError} for a stale token).
 *
 * @param {string|URL} url
 * @param {RequestInit} [init]
 * @returns {Promise<any>} the parsed JSON body
 */
export async function csrfFetchJson(url, init = {}) {
  try {
    const response = await csrfFetch(url, init);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (data?.error_csrf) {
      throw new CsrfRejectedError();
    }

    return data;
  } catch (error) {
    if (error.name !== 'AbortError') {
      logger.logError('Error while doing fetch call: ' + error.message, { url }, error);
    }
    throw error;
  }
}

/**
 * csrfFetchJson plus the feedback dialog on failure. A stale CSRF token always shows the reload
 * message, since retrying won't help; other failures show `errorMessage`, or the generic message
 * when omitted. Rejects after showing the dialog so an awaiting caller's flow halts.
 *
 * @param {string|URL} url
 * @param {RequestInit} [init]
 * @param {string} [errorMessage] user-facing message for non-CSRF failures
 * @returns {Promise<any>} the parsed JSON body
 */
export async function csrfFetchJsonWithFeedback(url, init = {}, errorMessage) {
  try {
    return await csrfFetchJson(url, init);
  } catch (error) {
    showFetchErrorDialog(error instanceof CsrfRejectedError ? RELOAD_MESSAGE : errorMessage);
    throw error;
  }
}

/** Generic, non-technical error message in the feedback dialog. */
export function showFetchErrorDialog(message = 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.') {
  createFeedbackDialog('feedback_error', message);
}
